import { __rest } from '../../_virtual/_tslib.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { styled, alpha } from '@mui/material';
import { useTreeItem } from '@mui/x-tree-view';
import { TreeItem, treeItemClasses } from '@mui/x-tree-view/TreeItem';
import { TreeView } from '@mui/x-tree-view/TreeView';
import clsx from 'clsx';
import { useRef, forwardRef } from 'react';
import { shouldForwardPropHelper } from '../../utils/shouldForwardHelper.js';
import '../Icons/Add.js';
import '../Icons/Application.js';
import '../Icons/AppStore.js';
import '../Icons/Arrow.js';
import '../Icons/At.js';
import '../Icons/Attach.js';
import '../Icons/Bank.js';
import '../Icons/Battery.js';
import '../Icons/Bell.js';
import '../Icons/Blocked.js';
import '../Icons/Calendar.js';
import '../Icons/Card.js';
import CaretDown from '../Icons/CaretDown.js';
import '../Icons/CaretLeft.js';
import CaretRight from '../Icons/CaretRight.js';
import '../Icons/CaretUp.js';
import '../Icons/Checkbox.js';
import '../Icons/CheckboxChecked.js';
import '../Icons/CheckboxIndeterminate.js';
import '../Icons/ChevronLeft.js';
import '../Icons/ChevronRight.js';
import '../Icons/Company.js';
import '../Icons/Copy.js';
import '../Icons/Currency.js';
import '../Icons/Customers.js';
import '../Icons/Dash.js';
import '../Icons/Dashboard.js';
import '../Icons/DirectionDown.js';
import '../Icons/DirectionUp.js';
import '../Icons/Document.js';
import '../Icons/DoubleChevronLeft.js';
import '../Icons/DoubleChevronRight.js';
import '../Icons/DoubleDirectionDown.js';
import '../Icons/DoubleDirectionUp.js';
import '../Icons/Download.js';
import '../Icons/Edit.js';
import '../Icons/ExclamationMark.js';
import '../Icons/ExclamationMarkFilled.js';
import '../Icons/ExternalLink.js';
import '../Icons/EyeClosed.js';
import '../Icons/EyeOpen.js';
import '../Icons/Fingerprint.js';
import '../Icons/Folder.js';
import '../Icons/Forever.js';
import '../Icons/Funnel.js';
import '../Icons/GooglePlay.js';
import '../Icons/Iban.js';
import '../Icons/Id.js';
import '../Icons/Info.js';
import '../Icons/InfoFilled.js';
import '../Icons/Instant.js';
import '../Icons/Ip.js';
import '../Icons/Link.js';
import '../Icons/List.js';
import '../Icons/Mail.js';
import '../Icons/Menu.js';
import '../Icons/Message.js';
import '../Icons/MessageDot.js';
import '../Icons/Onboarding.js';
import '../Icons/Padlock.js';
import '../Icons/PadlockOpen.js';
import '../Icons/Percent.js';
import '../Icons/Play.js';
import '../Icons/PowerOff.js';
import '../Icons/ProcessCompleted.js';
import '../Icons/ProcessCountEight.js';
import '../Icons/ProcessCountFive.js';
import '../Icons/ProcessCountFour.js';
import '../Icons/ProcessCountNineAndMore.js';
import '../Icons/ProcessCountOne.js';
import '../Icons/ProcessCountSeven.js';
import '../Icons/ProcessCountSix.js';
import '../Icons/ProcessCountThree.js';
import '../Icons/ProcessCountTwo.js';
import '../Icons/ProcessFailed.js';
import '../Icons/ProcessInProgress.js';
import '../Icons/ProcessNotStarted.js';
import '../Icons/ProcessPartialInProgress.js';
import '../Icons/ProcessPaused.js';
import '../Icons/Profile.js';
import '../Icons/Progress.js';
import '../Icons/Provider.js';
import '../Icons/QuestionMark.js';
import '../Icons/QuestionMarkFilled.js';
import '../Icons/Remove.js';
import '../Icons/Repeat.js';
import '../Icons/Reset.js';
import '../Icons/RoundArrow.js';
import '../Icons/RoundArrowFilled.js';
import '../Icons/RoundCheckOutlined.js';
import '../Icons/RoundCheckOutlinedFilled.js';
import '../Icons/RoundClose.js';
import '../Icons/RoundCloseFilled.js';
import '../Icons/RoundLeft.js';
import '../Icons/RoundLeftFilled.js';
import '../Icons/RoundMinus.js';
import '../Icons/RoundMinusFilled.js';
import '../Icons/RoundPlus.js';
import '../Icons/RoundPlusFilled.js';
import '../Icons/RoundRight.js';
import '../Icons/RoundRightFilled.js';
import '../Icons/Settings.js';
import '../Icons/SettingsCog.js';
import '../Icons/Shield.js';
import '../Icons/StarFilled.js';
import '../Icons/StarOutlined.js';
import '../Icons/Success.js';
import '../Icons/Timer.js';
import '../Icons/Trash.js';
import '../Icons/Upload.js';
import '../Icons/User.js';
import '../Icons/VerticalDots.js';
import '../Icons/World.js';
import '../Icons/Wrench.js';
import '../Icons/Zoom.js';

const DEPTH_PROPS_SET = new Set(["nodeDepth"]);
const Item = styled((props) => jsx(TreeItem, Object.assign({}, props)), {
    shouldForwardProp: (prop) => shouldForwardPropHelper(prop, DEPTH_PROPS_SET),
})(({ theme, nodeDepth }) => ({
    [`& .${treeItemClasses.group}`]: {
        marginLeft: 0,
        [`& .${treeItemClasses.content}`]: {
            paddingLeft: theme.spacing(3.5 * nodeDepth + 1),
            [`&.${SELECTABLE_CONTENT_CLASS}`]: {
                paddingLeft: theme.spacing(2.5 * nodeDepth + 1),
            },
        },
    },
    [`& .${treeItemClasses.content}`]: {
        padding: theme.spacing(1, 1.5),
        [`&.${SELECTABLE_CONTENT_CLASS}`]: {
            padding: theme.spacing(1.5, 1),
        },
        "& .MuiTreeItem-iconContainer": {
            margin: 0,
            width: 20,
            height: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        "& .MuiTreeItem-label": {
            paddingLeft: theme.spacing(1.5),
            lineHeight: 0,
            color: theme.palette.text.high,
        },
        "&.Mui-focused, &.Mui-selected.Mui-focused": {
            backgroundColor: "transparent",
        },
        "&:hover, &.Mui-selected:hover, &.Mui-focused:hover, &.Mui-selected.Mui-focused:hover": {
            backgroundColor: theme.palette.ui30,
        },
        [`&.${SELECTABLE_CONTENT_CLASS}.${SELECTABLE_EXPANDABLE_CONTENT_CLASS} .MuiTreeItem-label`]: {
            paddingLeft: theme.spacing(1),
        },
        [`&.${SELECTABLE_CONTENT_CLASS}.${SELECTABLE_NON_EXPANDABLE_CONTENT_CLASS} .MuiTreeItem-label`]: {
            paddingLeft: 0,
        },
        [`&.${SELECTABLE_CONTENT_CLASS}.Mui-focused,
      &.${SELECTABLE_CONTENT_CLASS}.Mui-selected.Mui-focused`]: {
            borderRadius: 4,
            backgroundColor: alpha(theme.palette.primary["800"], 0.05),
        },
        [`&.${SELECTABLE_CONTENT_CLASS}:hover,
      &.${SELECTABLE_CONTENT_CLASS}.Mui-selected:hover
      &.${SELECTABLE_CONTENT_CLASS}.Mui-focused:hover,
      &.${SELECTABLE_CONTENT_CLASS}.Mui-selected.Mui-focused:hover`]: {
            borderRadius: 4,
            backgroundColor: alpha(theme.palette.primary["800"], 0.05),
        },
        "&.Mui-selected": {
            backgroundColor: "transparent",
        },
        "&.Mui-disabled": {
            opacity: 1,
            "& .MuiTreeItem-label": {
                opacity: 0.4,
            },
        },
    },
}));
const ItemContentWrapper = styled("div", {
    shouldForwardProp: (prop) => shouldForwardPropHelper(prop, DEPTH_PROPS_SET),
})(({ theme, nodeDepth }) => ({
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1, 3.5 * nodeDepth + 1.5),
}));
function View(_a) {
    var { children, defaultSelected, defaultExpanded, expandIcon = jsx(CaretRight, {}), collapseIcon = jsx(CaretDown, {}), "data-testid": dataTestId, onNodeSelect, selected, onNodeFocus, onNodeToggle } = _a, rest = __rest(_a, ["children", "defaultSelected", "defaultExpanded", "expandIcon", "collapseIcon", "data-testid", "onNodeSelect", "selected", "onNodeFocus", "onNodeToggle"]);
    const treeRef = useRef(null);
    const { onFocusCapture } = useRedirectFocusToFirstElement(treeRef);
    return (jsx(TreeView, Object.assign({ "data-testid": dataTestId, defaultCollapseIcon: collapseIcon, defaultExpandIcon: expandIcon, defaultExpanded: defaultExpanded, multiSelect: false, ref: treeRef, sx: { flexGrow: 1, overflowY: "auto" }, defaultSelected: defaultSelected /* We want to have a multi-select with the API of single-select, so we ignore TS here */, selected: selected /* We want to have a multi-select with the API of single-select, so we ignore TS here. */, onFocusCapture: onFocusCapture, onNodeFocus: (event, id) => {
            var _a;
            if (event.target instanceof Element) {
                const focusedElement = (_a = treeRef.current) === null || _a === void 0 ? void 0 : _a.querySelector(`*[id="${event.target.id}-${id}"]`);
                focusedElement === null || focusedElement === void 0 ? void 0 : focusedElement.scrollIntoView({ block: "nearest" });
            }
            onNodeFocus === null || onNodeFocus === void 0 ? void 0 : onNodeFocus(event, id);
        }, onNodeSelect: (_, id) => onNodeSelect === null || onNodeSelect === void 0 ? void 0 : onNodeSelect(id), onNodeToggle: (_, ids) => onNodeToggle === null || onNodeToggle === void 0 ? void 0 : onNodeToggle(ids) }, rest, { children: children })));
}
// When we dispatch focus event targeting MuiTreeItem, MUI automatically dispatches new focus event on MuiTreeView.
// Because of that we need to make sure to block all focus events targeting MuiTreeView and make sure we don't trigger infinite focus loop.
const useRedirectFocusToFirstElement = (treeRef) => {
    const alreadyRedirectedToFirstElement = useRef(false);
    const onFocusCapture = (event) => {
        if (event.target !== treeRef.current) {
            alreadyRedirectedToFirstElement.current = true;
            return;
        }
        if (!alreadyRedirectedToFirstElement.current) {
            focusFirstElement(treeRef.current);
        }
        else {
            alreadyRedirectedToFirstElement.current = false;
        }
        event.stopPropagation();
    };
    return { onFocusCapture };
};
const focusFirstElement = (treeRoot) => {
    const firstTreeItem = treeRoot === null || treeRoot === void 0 ? void 0 : treeRoot.querySelector('*[role="treeitem"]');
    firstTreeItem === null || firstTreeItem === void 0 ? void 0 : firstTreeItem.focus();
};
const SELECTABLE_CONTENT_CLASS = "Volt-TreeSelectableContent";
const SELECTABLE_EXPANDABLE_CONTENT_CLASS = "Volt-TreeSelectableExpandableContent";
const SELECTABLE_NON_EXPANDABLE_CONTENT_CLASS = "Volt-TreeSelectableNonExpandableContent";
const TreeNodeContent = (options = {}) => forwardRef(function CustomContentImpl(props, ref) {
    var _a;
    const { isExpandable = true, handleExpansionOnRowClick = false } = options;
    const { classes, className, label, nodeId, icon: iconProp, expansionIcon, displayIcon } = props;
    const { disabled, expanded, selected, focused, handleExpansion, handleSelection, preventSelection, } = useTreeItem(nodeId);
    const icon = (_a = iconProp !== null && iconProp !== void 0 ? iconProp : expansionIcon) !== null && _a !== void 0 ? _a : displayIcon;
    const handleRowClick = (event) => {
        if (handleExpansionOnRowClick && isExpandable) {
            handleExpansion(event);
            event.stopPropagation();
        }
    };
    const handleLabelClick = (event) => {
        if (!handleExpansionOnRowClick) {
            handleSelection(event);
        }
    };
    return (jsxs("div", { ref: ref, className: clsx(SELECTABLE_CONTENT_CLASS, className, classes.root, {
            [SELECTABLE_EXPANDABLE_CONTENT_CLASS]: isExpandable,
            [SELECTABLE_NON_EXPANDABLE_CONTENT_CLASS]: !isExpandable,
            [classes.expanded]: expanded,
            [classes.selected]: selected,
            [classes.focused]: focused,
            [classes.disabled]: disabled,
        }), onClick: handleRowClick, onMouseDown: preventSelection, children: [isExpandable && (jsx("div", { className: classes.iconContainer, onClick: handleExpansion, children: icon })), jsx("div", { className: classes.label, onClick: handleLabelClick, children: label })] }));
});
/**
 * @deprecated Use `TreeNodeContent({isExpandable: true})` instead.
 */
const SelectableExpandableContent = TreeNodeContent({ isExpandable: true });
/**
 * @deprecated Use `TreeNodeContent({isExpandable: false})` instead.
 */
const SelectableNonExpandableContent = TreeNodeContent({ isExpandable: false });

export { Item, ItemContentWrapper, SelectableExpandableContent, SelectableNonExpandableContent, TreeNodeContent, View };
//# sourceMappingURL=Tree.js.map
